import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Modal,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  debounce,
  FormControl,
  Select,
  InputLabel,
  FormGroup,
  Stack,
  styled,
} from "@mui/material";
import axios from "axios";
import CustomTable from "../components/CustomTable";
import { useNavigate, useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import EditableJson from "../components/EditableJson";
import to from "await-to-js";
import Grid from "@mui/material/Grid2";
import FroalaEditorComponent from "../components/Editor";
import { axiosWithAuth } from "../plugins/axios";
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridColumnMenuContainer,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnMenuHideItem,
  getGridStringOperators,
  GridFilterModel,
} from "@mui/x-data-grid-pro";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Papa from "papaparse";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  bgcolor: "background.paper",
  overflowY: "scroll",
  height: "80vh",
  p: 4,
};

const commonIconButtonSx = {
  color: "black",
  transition: "transform 0.2s, color 0.2s",
  ":hover": {
    transform: "scale(1.2)",
    color: "primary.main",
  },
};

function CustomColumnMenu(props: GridColumnMenuProps) {
  const { hideMenu, colDef, open } = props;
  return (
    <GridColumnMenuContainer hideMenu={hideMenu} colDef={colDef} open={open}>
      <GridColumnMenuSortItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuHideItem colDef={colDef} onClick={hideMenu} />
    </GridColumnMenuContainer>
  );
}

function Quizzes() {
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingQuizId, setEditingQuizId] = useState<number | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editingRubrics, setEditingRubrics] = useState<any>({});
  const [editingProblemStatement, setEditingProblemStatement] =
    useState<string>("");
  const [cohortDetails, setCohortDetails] = useState<any>({});
  const [showEditor, setShowEditor] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [open, setOpen] = useState<string | null>(null);

  const [publishingQuizId, setPublishingQuizId] = useState<string | null>(null);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: rowsPerPage,
    page: page,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState<
    "publish" | "unpublish" | "delete"
  >("publish");
  const [isCrawling, setIsCrawling] = useState<boolean>(false);
  const [isProblemStatementChanged, setIsProblemStatementChanged] =
    useState<boolean>(false);
  const [submissionTypes, setSubmissionTypes] = useState<any[]>([]);
  const [submissionTypeModalOpen, setSubmissionTypeModalOpen] = useState(false);
  const [selectedSubmissionType, setSelectedSubmissionType] =
    useState<any>(null);
  const [selectedQuiz, setSelectedQuiz] = useState<any>(null);

  const apiUrl = process.env.REACT_APP_PIPELINE_API;

  const { cohortId } = useParams();
  const navigate = useNavigate();

  const debounceFetchQuizzes = useMemo(
    () =>
      debounce((filter) => {
        fetchQuizzes(filter);
      }, 1000),
    [sortModel]
  );
  const handleFilterChange = (filterModel: GridFilterModel) => {
    const filters =
      filterModel.items.map((item) => ({
        column: item.field,
        value: item.value,
        operator: item.operator,
      })) || [];
    const filter = {
      //   filterColumn: filterModel.items[0]?.field,
      //   filterValue: filterModel.items[0]?.value,
      //   filterOperator: filterModel.items[0]?.operator,
      filters: JSON.stringify(filters),
    };
    debounceFetchQuizzes(filter);
  };

  const fetchQuizzes = async (filter?: any) => {
    try {
      const response = await axiosWithAuth(
        `${apiUrl}cohortconfiguration/quizzes/${cohortId}`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            sortColumn: sortModel[0]?.field || null,
            sortDirection: sortModel[0]?.sort || null,
            ...filter,
          },
        },
        "get"
      );
      const { data, totalCount } = response.data;
      const updatedData = data.map((quiz: any) => {
        return {
          ...quiz,
          rubrics: quiz.updatedRubrics || quiz.rubrics,
        };
      });
      setQuizzes(updatedData);
      setTotalCount(totalCount);
    } catch (err) {
      setError("Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => debounceFetchQuizzes.clear();
  }, [debounceFetchQuizzes]);

  const fetchCourseDetails = async () => {
    const [err, response] = await to(
      axiosWithAuth(
        `${apiUrl}cohortConfiguration/cohort/${cohortId}`,
        {},
        "get"
      )
    );

    if (err) {
      setError("Failed to load course details");
      setLoading(false);
      return;
    }

    setCohortDetails(response!.data);
  };

  useEffect(() => {
    fetchCourseDetails();
    fetchQuizzes();
    fetchSubmissionTypes();
  }, [apiUrl, page, rowsPerPage, sortModel]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const fetchSubmissionTypes = async () => {
    const [err, response] = await to(
      axiosWithAuth(`${apiUrl}cohortConfiguration/submissiontypes`, {}, "get")
    );

    if (err) {
      setToastMessage("Failed to load submission types");
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      setSubmissionTypes(response.data?.data || []);
    }
  };

  const handlePublishQuiz = async (id: string) => {
    setPublishingQuizId(id);

    setIsSubmitting(true);
    const [publishError] = (await to(
      axiosWithAuth(`${apiUrl}cohortconfiguration/publish/${id}`, {}, "post")
    )) as any;

    // const [publishError] = await to(axios.post(`${apiUrl}cohortconfiguration/publish/${id}`, {})) as any;

    if (publishError) {
      const { data: { message = null } = {} } = publishError.response;
      setToastMessage(message);
      setToastSeverity("error");

      setToastOpen(true);
    } else {
      setQuizzes((prevQuizzes) =>
        prevQuizzes.map((quiz) =>
          quiz.quizId === id ? { ...quiz, status: "published" } : quiz
        )
      );
    }
    setPublishingQuizId(null);
    setIsSubmitting(false);
  };

  const handleRun = async (id: string) => {
    alert(`Running evaluation for Quiz ${id}`);
  };

  const handleEditRubrics = (id: any) => {
    setEditingQuizId(id);
    const quiz = quizzes.find((quiz) => quiz.quizId === id);
    setEditingRubrics(quiz.rubrics);
    setOpen("rubrics");
  };

  const handleOpenSubmissionTypeModal = (quiz: any) => {
    setSelectedQuiz(quiz);
    setSelectedSubmissionType(quiz.settings?.submissionType || null);
    setSubmissionTypeModalOpen(true);
  };

  const handleCloseSubmissionTypeModal = () => {
    setSubmissionTypeModalOpen(false);
    setSelectedQuiz(null);
    setSelectedSubmissionType(null);
  };

  const handleSubmissionTypeChange = async (submissionTypeId: string) => {
    if (!selectedQuiz) return;

    const newSubmissionType = submissionTypes.find(
      (st) => st._id === submissionTypeId
    );
    if (!newSubmissionType) return;

    console.log(selectedQuiz.quizId, "you v", newSubmissionType);

    setIsSubmitting(true);
    try {
      await axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${selectedQuiz.quizId}`,
        {
          settings: {
            ...selectedQuiz.settings,
            submissionType: newSubmissionType,
          },
        },
        "put"
      );
      setToastMessage("Submission type updated successfully");
      setToastSeverity("success");
      fetchQuizzes();
    } catch (error) {
      setToastMessage("Failed to update submission type");
      setToastSeverity("error");
    } finally {
      setIsSubmitting(false);
      setToastOpen(true);
      handleCloseSubmissionTypeModal();
    }
  };

  // const downloadRubrics = () => {
  //   const dataStr =
  //     "data:text/json;charset=utf-8," +
  //     encodeURIComponent(JSON.stringify(editingRubrics, null, 2));
  //   const downloadAnchorNode = document.createElement("a");
  //   downloadAnchorNode.setAttribute("href", dataStr);
  //   downloadAnchorNode.setAttribute(
  //     "download",
  //     `rubrics_${editingQuizId}.json`
  //   );
  //   downloadAnchorNode.click();
  //   downloadAnchorNode.remove();
  // };

  const triggerCrawling = async (content: string) => {
    setIsCrawling(true);
    const [crawlError, crawlData] = await to(
      axiosWithAuth(
        `${apiUrl}autoGrade/crawl`,
        {
          input: {
            content: content.trim(),
          },
        },
        "post"
      )
    );

    if (crawlError) {
      setToastMessage(crawlError?.message);
      setToastOpen(true);
      setIsCrawling(false);
      return;
    }
    console.log(crawlData?.data?.output);
    setEditingProblemStatement(crawlData?.data?.output);
    setIsCrawling(false);
    setIsProblemStatementChanged(false);
  };

  function processRubricToCsv(json: any): string {
    const rows: string[] = [];
    const printedL1 = new Set<string>();
    const printedL2 = new Set<string>();

    rows.push(
      '"Level 1","Level 2","Level 3","Max Marks","Comments","Marks","Grading Guidelines"'
    );

    function processNode(
      node: any,
      l1: any = {},
      l2: any = {},
      prevL1Id: string | null = null,
      prevL2Id: string | null = null
    ): void {
      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any) => {
          if (!l1.id) {
            processNode(
              child,
              { id: node.id, name: node.name, maxMarks: node.maxMarks },
              {},
              null,
              null
            );
          } else if (!l2.id) {
            processNode(
              child,
              l1,
              { id: node.id, name: node.name, maxMarks: node.maxMarks },
              l1.id,
              null
            );
          } else {
            processNode(child, l1, l2, l1.id, l2.id);
          }
        });
      } else {
        if (!l1.name) {
          rows.push(
            `"${node.name || ""}","","","${node.maxMarks || ""}","","","${(
              node.gradingGuideline || ""
            )
              .replace(/\n/g, " ")
              .replace(/"/g, '""')}"`
          );
        } else if (!l2.name) {
          if (!l1.name || !printedL1.has(l1.name)) {
            rows.push(
              `"${l1.name || ""}","","","${l1.maxMarks || ""}","","",""`
            );
            printedL1.add(l1.name);
          }
          rows.push(
            `"${l1.name || ""}","${node.name || ""}","","${
              node.maxMarks || ""
            }","","","${(node.gradingGuideline || "")
              .replace(/\n/g, " ")
              .replace(/"/g, '""')}"`
          );
        } else {
          if (!l1.name || !printedL1.has(l1.name)) {
            rows.push(
              `"${l1.name || ""}","","","${l1.maxMarks || ""}","","",""`
            );
            printedL1.add(l1.name);
          }

          if (!l2.name || !printedL2.has(l2.name)) {
            rows.push(
              `"${l1.name || ""}","${l2.name || ""}","","${
                l2.maxMarks || ""
              }","","","",""`
            );
            printedL2.add(l2.name);
          }

          rows.push(
            `"${l1.name || ""}","${l2.name || ""}","${node.name || ""}","${
              node.maxMarks || ""
            }","","","${(node.gradingGuideline || "")
              .replace(/\n/g, " ")
              .replace(/"/g, '""')}"`
          );
        }
      }
    }

    json.rubric.children.forEach((child: any) => {
      processNode(child);
    });

    return rows.join("\n");
  }

  const downloadRubrics = () => {
    const csvOutput = processRubricToCsv(editingRubrics);
    const dataStr =
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvOutput);
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `rubrics_${editingQuizId}.csv`);

    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  // const handleUploadRubrics = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];
  //   setEditingRubrics({});
  //   if (file) {
  //     const text = await file.text();
  //     try {
  //       const parsedRubrics = JSON.parse(text);
  //       setEditingRubrics(parsedRubrics);
  //     } catch (error) {
  //       setToastMessage("Invalid JSON format");
  //       setToastOpen(true);
  //     }
  //   }
  // };

  function validateHierarchy(node: any, level: number) {
    if (level === 1 && (!node.children || node.children.length === 0)) {
      throw new Error(
        `Validation Error: Level 1 node "${node.name}" has no Level 2 children.`
      );
    }
    if (level === 2 && (!node.children || node.children.length === 0)) {
      throw new Error(
        `Validation Error: Level 2 node "${node.name}" has no Level 3 children.`
      );
    }
    if (node.children) {
      node.children.forEach((child: any) =>
        validateHierarchy(child, level + 1)
      );
    }
  }

  function validateChildMarks(node: any) {
    if (node.children && node.children.length > 0) {
      const childrenSum = node.children.reduce(
        (sum: number, child: any) => sum + child.maxMarks,
        0
      );

      if (node.maxMarks !== childrenSum) {
        throw new Error(
          `Validation Error: Max marks of node "${node.name}" (ID: ${node.id}) is ${node.maxMarks}, which does not match the sum of its children's marks (${childrenSum}).`
        );
      }

      node.children.forEach(validateChildMarks);
    }
  }

  function updateMaxMarks(node: any) {
    if (node.children) {
      node.maxMarks = node.children.reduce((sum: number, child: any) => {
        updateMaxMarks(child);
        return sum + child.maxMarks;
      }, 0);
    }
  }

  const processCsvToHierarchy = async (csvData: string) => {
    const hierarchy = {
      rubric: {
        id: 0,
        children: [],
        maxMarks: 0,
      },
      published: editingRubrics?.published || false,
      canBeUnpublished: editingRubrics?.canBeUnpublished || false,
      questionId: editingRubrics?.questionId || 0,
      questionTitle: editingRubrics?.questionTitle || "",
    };

    const idCounter = { id: Math.floor(1000000 + Math.random() * 9000000) };

    function getNextId() {
      return idCounter.id++;
    }

    function findOrCreateLevel(
      levelArray: any[],
      name: string,
      maxMarks: number | null,
      isLeaf = false
    ) {
      let existing = levelArray.find((item) => item.name === name);
      if (!existing) {
        existing = {
          id: getNextId(),
          name: name,
          maxMarks: maxMarks || 0,
          gradingGuideline: null,
          ...(isLeaf ? {} : { children: [] }),
        };
        levelArray.push(existing);
      }
      return existing;
    }

    return new Promise((resolve, reject) => {
      Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          try {
            const rows = result.data;

            rows.forEach((row: any) => {
              const level1 = row["Level 1"];
              const level2 = row["Level 2"];
              const level3 = row["Level 3"];
              const maxMarks = parseInt(row["Max Marks"] || "0", 10);
              const gradingGuideline = row["Grading Guidelines"] || null;

              let level1Node = findOrCreateLevel(
                hierarchy.rubric.children,
                level1,
                null
              );

              let level2Node = null;
              if (level2) {
                level2Node = findOrCreateLevel(
                  level1Node.children,
                  level2,
                  null
                );
              }

              if (level3) {
                const level3Node = findOrCreateLevel(
                  level2Node.children,
                  level3,
                  maxMarks,
                  true
                );
                level3Node.gradingGuideline = gradingGuideline;
              }
            });

            hierarchy.rubric.children.forEach((child: any) =>
              validateHierarchy(child, 1)
            );
            updateMaxMarks(hierarchy.rubric);
            validateChildMarks(hierarchy.rubric);

            resolve(hierarchy);
          } catch (error) {
            reject(error);
          }
        },
        error: (error: Error) => {
          reject(error);
        },
      });
    });
  };

  const handleUploadRubrics = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    setEditingRubrics({});

    if (file) {
      try {
        const text = await file.text();
        let parsedRubrics;

        if (file.name.endsWith(".csv")) {
          parsedRubrics = await processCsvToHierarchy(text);
        } else {
          throw new Error("Unsupported file format");
        }

        setEditingRubrics(parsedRubrics);
      } catch (error) {
        if (error instanceof Error) {
          setToastMessage(error.message);
          setToastSeverity("error");
          setToastOpen(true);
        } else {
          setToastMessage("Invalid file format or content");
          setToastSeverity("error");
          setToastOpen(true);
        }
      }
    }
  };
  const toggleEditor = () => setShowEditor((prev) => !prev);

  const handleEditProblemStatement = (id: any) => {
    setEditingQuizId(id);
    const quiz = quizzes.find((quiz) => quiz.quizId === id);
    setEditingProblemStatement(quiz.problemStatement);
    setOpen("problemStatement");
  };

  const handleClose = () => {
    setOpen(null);
  };

  const saveRubrics = async (rubrics: any) => {
    setIsSubmitting(true);

    const updatedRubrics = editingRubrics;

    updatedRubrics.rubric = rubrics;

    const [submitErr] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${editingQuizId}`,
        { rubrics: updatedRubrics },
        "put"
      )
    )) as any;

    if (submitErr) {
      const { data: { message = null } = {} } = submitErr.response;
      setToastMessage(message);
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      handleClose();
      fetchQuizzes();
    }
    setIsSubmitting(false);
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);

    const [submitErr] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${editingQuizId}`,
        { problemStatement: editingProblemStatement },
        "put"
      )
    )) as any;

    if (submitErr) {
      const { data: { message = null } = {} } = submitErr.response;
      setToastMessage(message);
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      handleClose();
      fetchQuizzes();
    }
    setIsSubmitting(false);
  };

  const handleToastClose = () => setToastOpen(false);

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    quizId: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedQuizId(quizId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedQuizId(null);
  };
  const handleConfirmationOpen = (type: "publish" | "unpublish" | "delete") => {
    setConfirmationType(type);
    setConfirmationOpen(true);
    handleMenuClose();
  };

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
  };

  const handlePublishUnpublishQuiz = async () => {
    const endpoint =
      confirmationType === "publish"
        ? `${apiUrl}cohortconfiguration/publish/${selectedQuizId}`
        : `${apiUrl}cohortconfiguration/unpublish/${selectedQuizId}`;

    try {
      await axiosWithAuth(endpoint, {}, "post");
      fetchQuizzes();
      setToastMessage(
        confirmationType === "publish"
          ? "Quiz published successfully"
          : "Quiz unpublished successfully"
      );
      setToastSeverity("success");
      setToastOpen(true);
    } catch (err: unknown) {
      setToastMessage("Action failed. Please try again.");
      setToastSeverity("error");
      setToastOpen(true);
    } finally {
      handleConfirmationClose();
    }
  };
  const handleDeleteQuiz = async () => {
    try {
      await axiosWithAuth(
        `${apiUrl}cohortconfiguration/delete/${selectedQuizId}`,
        {},
        "delete"
      );
      fetchQuizzes();
      setToastMessage("Quiz deleted successfully");
      setToastSeverity("success");
      setToastOpen(true);
    } catch (err: unknown) {
      setToastMessage("Failed to delete the quiz. Please try again.");
      setToastSeverity("error");
      setToastOpen(true);
    } finally {
      handleConfirmationClose();
    }
  };

  const handleMarkAsMaster = async (quizId: string) => {
    try {
      await axiosWithAuth(
        `${apiUrl}cohortConfiguration/update/${quizId}`,
        { masterSolution: true },
        "put"
      );

      fetchQuizzes();
      setToastMessage(
        "This configuration was successfully marked as a master solution"
      );
      setToastOpen(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // console.log(error);

        const errorMessage =
          error.response?.data?.message ||
          "Something went wrong. Please try again.";

        setToastMessage(errorMessage);
        setToastSeverity("error");
        setToastOpen(true);
      } else {
        setToastMessage("Failed to mark as master solution. Please try again.");
        setToastSeverity("error");
        setToastOpen(true);
      }
    } finally {
      handleConfirmationClose();
    }
  };

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 36,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(14px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(20px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "#1890ff",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      width: "100%",
      opacity: 1,
      backgroundColor: "rgba(0,0,0,.25)",
    },
  }));

  const handleToggleExtractLinks = async (
    quizId: string,
    shouldCrawl: boolean
  ) => {
    try {
      setQuizzes((prevQuizzes) =>
        prevQuizzes.map((quiz) =>
          quiz.quizId === quizId ? { ...quiz, crawlLinks: shouldCrawl } : quiz
        )
      );

      await axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${quizId}`,
        { crawlLinks: shouldCrawl },
        "put"
      );

      setToastMessage(
        `Link extraction ${shouldCrawl ? "enabled" : "disabled"}`
      );
      setToastSeverity("success");
    } catch (error) {
      setQuizzes((prevQuizzes) =>
        prevQuizzes.map((quiz) =>
          quiz.quizId === quizId ? { ...quiz, crawlLinks: !shouldCrawl } : quiz
        )
      );
      setToastMessage("Failed to update link extraction");
      setToastSeverity("error");
    } finally {
      setToastOpen(true);
    }
  };

  const handleViewGroup = (id: string) => {
    navigate(`/masterSolution/${id}/list-of-cohorts`);
  };

  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );

  const isPublished = editingQuizId
    ? quizzes.find((quiz) => quiz.quizId === editingQuizId)?.status ===
      "published"
    : false;

  const columns: GridColDef[] = [
    {
      field: "quizId",
      headerName: "ID",
      flex: 1,
      renderCell: (params) => (
        <Button
          color="primary"
          onClick={() =>
            navigate(`/cohort/${cohortId}/quiz/${params.value}/evaluations`)
          }
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: "quizName",
      headerName: "Quiz Name",
      flex: 2,
      renderCell: (params) => {
        const quiz = quizzes.find((quiz) => quiz.quizName === params.value);
        return (
          <Button
            color="primary"
            onClick={() =>
              quiz
                ? navigate(
                    `/cohort/${cohortId}/quiz/${quiz.quizId}/evaluations`
                  )
                : console.error("Quiz not found")
            }
            sx={{ textTransform: "none" }}
          >
            {params.value}
          </Button>
        );
      },
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "teachUrl",
      headerName: "Teach Link",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            Link
          </a>
        ) : (
          "-"
        ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      flex: 1,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          variant="outlined"
          sx={{
            borderRadius: 10,
            padding: "8px 25px",
            borderColor: params.value === "published" ? "green" : "default",
            color: params.value === "published" ? "green" : "default",
          }}
        />
      ),
      filterOperators: getCustomFilterOperators(),
    },
    // {
    //   field: "settings",
    //   headerName: "Submission Type",
    //   flex: 1,
    //   renderCell: (params: GridRenderCellParams) => {
    //     let val: string | undefined = params.row.settings?.submissionName;

    //     return val || "-"
    //   },
    //   filterable: false,
    //   filterOperators: getCustomFilterOperators(),
    // },
    {
      field: "crawlLinks",
      headerName: "Extract Links",
      width: 150,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          <AntSwitch
            checked={Boolean(params.value)}
            onChange={(e) =>
              handleToggleExtractLinks(params.row.quizId, e.target.checked)
            }
            disabled={params.row.status === "published"}
          />
        </Box>
      ),
      filterable: false,
    },
    {
      field: "settings",
      headerName: "Submission Type",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const submissionName = params.row.settings?.submissionName;
        const isPublished = params.row.status === "published";

        return (
          <Box display="flex" alignItems="center">
            <IconButton
              sx={{
                ...commonIconButtonSx,
                marginRight: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenSubmissionTypeModal(params.row);
              }}
              disabled={isPublished}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {submissionName || "-"}
          </Box>
        );
      },
      filterable: false,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "actions",
      headerName: "",
      flex: 4,
      renderCell: (params) => {
        const isPublished = params.row.status === "published";
        return (
          <Box
            display="flex"
            gap={4}
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Button
              variant="text"
              style={{
                color: "#000",
                textTransform: "none",
              }}
              startIcon={<EditIcon />}
              onClick={() => handleEditProblemStatement(params.row.quizId)}
            >
              {isPublished
                ? "View Problem Statement"
                : "Edit Problem Statement"}
            </Button>
            <Button
              variant="text"
              style={{
                color: "#000",
                textTransform: "none",
              }}
              startIcon={<EditIcon />}
              onClick={() => handleEditRubrics(params.row.quizId)}
            >
              {isPublished ? "View Rubrics" : "Edit Rubrics"}
            </Button>
            <>
              <IconButton
                onClick={(event) => handleMenuOpen(event, params.row.quizId)}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(
                  menuAnchor && selectedQuizId === params.row.quizId
                )}
                onClose={handleMenuClose}
              >
                {params.row.status !== "published" && (
                  <MenuItem
                    onClick={() => {
                      handleConfirmationOpen("publish");
                      setSelectedQuizId(params.row.quizId);
                    }}
                  >
                    Publish
                  </MenuItem>
                )}
                {params.row.status === "published" && (
                  <MenuItem
                    onClick={() => {
                      handleConfirmationOpen("unpublish");
                      setSelectedQuizId(params.row.quizId);
                    }}
                  >
                    Un-Publish
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    handleConfirmationOpen("delete");
                    setSelectedQuizId(params.row.quizId);
                  }}
                >
                  Delete
                </MenuItem>
                <MenuItem
                  onClick={() => handleMarkAsMaster(params.row.quizId)}
                  // disabled={params?.row?.masterSolution}
                  disabled={
                    params?.row?.masterSolution ||
                    params?.row?.status !== "published" ||
                    params?.row?.cloneFrom
                  }
                >
                  {params.row.masterSolution
                    ? "Master Solution (Marked)"
                    : "Mark as Master Solution"}
                </MenuItem>

                <MenuItem
                  onClick={() =>
                    handleViewGroup(
                      params.row.masterSolution
                        ? params.row._id
                        : params.row.cloneFrom
                    )
                  }
                  disabled={!params.row.masterSolution && !params.row.cloneFrom}
                >
                  View Group
                </MenuItem>
              </Menu>
            </>
          </Box>
        );
      },
      filterOperators: getCustomFilterOperators(),
    },
  ];

  return (
    <Box mt={4}>
      <Box pb={4}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography gutterBottom>
              Cohort:{" "}
              <Typography display={"inline"} fontWeight={"bold"}>
                {cohortId}
              </Typography>
            </Typography>
            <Typography fontWeight={"bold"}>
              {cohortDetails.cohortName}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Typography variant="h4" gutterBottom>
        List of Quizzes
      </Typography>
      <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
        <DataGridPro
          rows={quizzes}
          columns={columns}
          pagination
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setRowsPerPage(model.pageSize);
          }}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          loading={loading}
          getRowId={(row) => row.quizId}
          rowHeight={70}
          pageSizeOptions={[5, 10, 25, 50]}
          // slots={{
          //   columnMenu: CustomColumnMenu,
          // }}
          onFilterModelChange={handleFilterChange}
        />
      </Box>

      <Dialog
        open={submissionTypeModalOpen}
        onClose={handleCloseSubmissionTypeModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Submission Type</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Submission Type</InputLabel>
            <Select
              value={selectedSubmissionType?._id || ""}
              onChange={(e) =>
                setSelectedSubmissionType(
                  submissionTypes.find((st) => st._id === e.target.value)
                )
              }
              label="Submission Type"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {submissionTypes.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmissionTypeModal}>Cancel</Button>
          <Button
            onClick={() =>
              selectedSubmissionType &&
              handleSubmissionTypeChange(selectedSubmissionType._id)
            }
            disabled={isSubmitting || !selectedSubmissionType}
            color="primary"
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmationOpen} onClose={handleConfirmationClose}>
        <DialogTitle
          sx={{
            backgroundColor: "#e0e0e0",
            color: "black",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        >
          {confirmationType === "publish"
            ? "Are you sure you want to publish this quiz?"
            : confirmationType === "unpublish"
            ? "Are you sure you want to un-publish this quiz?"
            : "Delete Quiz"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" pt={2}>
            {confirmationType === "publish"
              ? "If you wish to edit the details again, you have to unpublish the quiz."
              : confirmationType === "unpublish"
              ? "Un-publishing a quiz will stops the evaluaions done automatically for the submission made by the learners for all active cohorts."
              : "Are you sure you want to delete this quiz?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button
            variant="outlined"
            onClick={handleConfirmationClose}
            sx={{
              color: "black",
              borderColor: "black",
              width: "90px",
              fontSize: "14px",
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={
              confirmationType === "delete"
                ? handleDeleteQuiz
                : handlePublishUnpublishQuiz
            }
            sx={{
              backgroundColor: "black",
              color: "white",
              width: "90px",
              fontSize: "14px",
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={open !== null}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {open === "rubrics" ? (
          <Box sx={style}>
            <Typography
              variant="h4"
              gutterBottom
              justifyContent={"space-between"}
              alignItems={"center"}
              display={"flex"}
            >
              {isPublished ? "View Rubrics" : "Edit Rubrics"}
              <IconButton aria-label="close" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Typography>

            <Button variant="text" color="primary" onClick={downloadRubrics}>
              Download Rubrics
            </Button>
            <Box gap={2} mt={2} mb={2}>
              <Button
                variant="outlined"
                component="label"
                color="primary"
                disabled={isPublished}
              >
                Upload Rubrics
                <input type="file" hidden onChange={handleUploadRubrics} />
              </Button>
            </Box>
            {editingRubrics.rubric && (
              <EditableJson
                isSubmitting={isSubmitting}
                saveRubrics={(rubrics) =>
                  !isPublished ? saveRubrics(rubrics) : undefined
                }
                initialJson={editingRubrics.rubric}
                key={JSON.stringify(editingRubrics.rubric)}
                isPublished={isPublished}
              />
            )}
          </Box>
        ) : (
          <Box sx={style}>
            <Typography
              variant="h4"
              gutterBottom
              justifyContent={"space-between"}
              alignItems={"center"}
              display={"flex"}
            >
              {isPublished
                ? "View Problem statement"
                : "Edit Problem statement"}
              <IconButton aria-label="close" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Typography>
            {/* <TextField
              label="Problem Statement"
              name="problemStatement"
              value={editingProblemStatement}
              onChange={(e) => setEditingProblemStatement(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={35}
            /> */}
            <FroalaEditorComponent
              value={editingProblemStatement}
              onChange={(content) => {
                setEditingProblemStatement(content);
                setIsProblemStatementChanged(true);
              }}
              placeholder="Enter the problem statement..."
              height={"55vh"}
              isPublished={isPublished}
              readonly={isPublished}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => triggerCrawling(editingProblemStatement)}
              sx={{ mt: 2 }}
              disabled={isCrawling || !isProblemStatementChanged}
            >
              Extract Links
            </Button>
            <Box justifyContent="flex-end" display="flex">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleFormSubmit}
                disabled={isPublished || isSubmitting || isCrawling}
                sx={{ mt: 2 }}
              >
                {isSubmitting
                  ? "Saving..."
                  : isCrawling
                  ? "Extracting Links"
                  : "Submit"}
              </Button>
            </Box>
          </Box>
        )}
      </Modal>
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        {/* <Alert
          onClose={handleToastClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert> */}
        {toastSeverity === "success" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#4caf50",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 48 48"
              style={{ marginRight: "8px" }}
            >
              <path
                fill="#4caf50"
                d="M44,24c0,11.045-8.955,20-20,20S4,35.045,4,24S12.955,4,24,4S44,12.955,44,24z"
              ></path>
              <path
                fill="#ccff90"
                d="M34.602,14.602L21,28.199l-5.602-5.598l-2.797,2.797L21,33.801l16.398-16.402L34.602,14.602z"
              ></path>
            </svg>
            {toastMessage}
          </div>
        ) : (
          <Alert
            onClose={handleToastClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
export default Quizzes;
